import { isPublished } from "./interventions";
import { compareNatural } from "./text";
import type { Arena, ColumnDefinition, Intervention, View } from "./types";
import { UNCLASSIFIED_CODE } from "./types";

/** Arena number → label for workbooks whose Arena column is "1 - Skole" etc. */
const ARENA_BY_NUMBER: Record<string, { no: string; en: string }> = {
  "1": { no: "Skole", en: "School" },
  "2": { no: "Familie", en: "Family" },
  "3": { no: "Arbeidsplass", en: "Workplace" },
  "4": { no: "Helse", en: "Health" },
  "5": { no: "Justis", en: "Justice" },
  "6": { no: "Samfunn", en: "Community" },
  "7": { no: "Multisetting", en: "Multisetting" },
};

/** Label for a legacy numbered arena value, or the raw value itself. */
export function getLegacyArenaLabel(arena: string, language: string): string {
  const number = arena.trim().match(/^(\d+)/)?.[1];
  const entry = number ? ARENA_BY_NUMBER[number] : undefined;
  if (!entry) return arena;
  return language === "en" ? entry.en : entry.no;
}

export interface MatrixRow {
  arena: string;
  cells: Record<string, Intervention[]>;
}

export interface BuildMatrixOptions {
  items: Intervention[];
  columns: ColumnDefinition[];
  layoutId: string;
  view: View;
  /** Registered arenas (database mode); `null` derives rows from the data. */
  arenas: Arena[] | null;
  /** Data file name; the RePS workbook hides the justice arena. */
  dataFile: string | null;
  isVisible: (item: Intervention) => boolean;
}

/** Orders items by their code in the selected view, unclassified last. */
function compareByViewCode(view: View) {
  return (a: Intervention, b: Intervention): number => {
    const ca = a.codes[view.id] ?? "";
    const cb = b.codes[view.id] ?? "";
    if (!ca && cb) return 1;
    if (!cb && ca) return -1;
    return ca.localeCompare(cb, undefined, { numeric: true });
  };
}

/** Matrix row keys in display order. */
export function getVisibleArenas(
  itemArenas: string[],
  arenas: Arena[] | null,
  dataFile: string | null,
): string[] {
  if (arenas) {
    const registered = [...arenas]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((arena) => arena.key);
    const legacy = itemArenas.filter((key) => !registered.includes(key)).sort(compareNatural);
    return [...registered, ...legacy];
  }
  const rows = [...itemArenas].sort(compareNatural);
  if (dataFile === "RePS") return rows.filter((row) => !/^(5\s*-\s*)?justis$/i.test(row));
  return rows;
}

/**
 * Builds the arena × column grid of published interventions that pass the
 * current filters. An item spanning several columns appears in each of them.
 */
export function buildMatrix(options: BuildMatrixOptions): MatrixRow[] {
  const { items, columns, layoutId, view, arenas, dataFile, isVisible } = options;
  const published = items.filter((item) => item.arena && isPublished(item));
  const byArena = new Map<string, Record<string, Intervention[]>>();
  for (const item of published) {
    for (const columnKey of new Set(item.columnLayoutKeys[layoutId] ?? [])) {
      const cells = byArena.get(item.arena) ?? {};
      (cells[columnKey] ??= []).push(item);
      byArena.set(item.arena, cells);
    }
  }
  const compare = compareByViewCode(view);
  return getVisibleArenas([...byArena.keys()], arenas, dataFile).map((arena) => {
    const source = byArena.get(arena) ?? {};
    const cells = Object.fromEntries(
      columns.map((column) => [
        column.key,
        (source[column.key] ?? []).filter(isVisible).sort(compare),
      ]),
    );
    return { arena, cells };
  });
}

/** Value codes rendered in the matrix, in legend order (unclassified last). */
export function getLegendCodes(rows: MatrixRow[], view: View): string[] {
  const seen = new Set<string>();
  for (const row of rows) {
    for (const items of Object.values(row.cells)) {
      for (const item of items) {
        const code = item.codes[view.id];
        seen.add(code && view.values[code] ? code : UNCLASSIFIED_CODE);
      }
    }
  }
  const codes = view.order.filter((code) => seen.has(code));
  if (seen.has(UNCLASSIFIED_CODE)) codes.push(UNCLASSIFIED_CODE);
  return codes;
}

/** Text shown inside a matrix box depending on the ID/label toggles. */
export function getItemLabel(
  item: Intervention,
  language: string,
  showIds: boolean,
  showTitles: boolean,
): string {
  const id = item.itemNumber.trim();
  const title = (language === "en" ? item.display.en : item.display.no) || item.display.no;
  if (showIds && showTitles) return id && title ? `${id} - ${title}` : id || title;
  if (showIds) return id;
  if (showTitles) return title;
  return "";
}
